import React, { useEffect, useMemo, useRef, useState } from "react";
import { Segmented } from "./ui";
import { AppShell } from "./shell/AppShell";
import { SidebarSection } from "./shell/Sidebar";
import Resumen from "./tabs/Resumen";
import Discipline from "./tabs/Discipline";
import BodyComposition from "./tabs/BodyComposition";
import Material from "./tabs/Material";
import Carga from "./tabs/Carga";
import Salud, { HealthView } from "./tabs/Salud";
import Coach from "./tabs/Coach";
import Calendario from "./tabs/Calendario";
import MyCoach from "./MyCoach";
import CoachRoster from "./CoachRoster";
import CoachAthleteView from "./CoachAthleteView";
import CoachInbox from "./CoachInbox";
import CoachLinkCodes from "./CoachLinkCodes";
import CoachCalendar from "./CoachCalendar";
import DirectChat from "./DirectChat";
import FooterModule from "./Footer";
import { AddDisciplineMenu } from "./AddDisciplineMenu";
import { EmptyState } from "./EmptyState";
import { DashboardData } from "@/lib/types";
import { disciplinesByActivityCount, normGearId } from "@/lib/dash";
import { catAccentRGB } from "@/lib/format";
import { useUI } from "@/lib/theme";
import { ApiError, api, coachApi } from "@/api";
import { DEMO_COACH } from "@/lib/demoData";

const HEALTH_TABS: HealthView[] = ["s_resumen", "s_cuerpo", "s_sueno", "s_cruce"];

const RANGES: { key: string; days: number | null }[] = [
  { key: "range_all", days: null },
  { key: "range_12m", days: 365 },
  { key: "range_3m", days: 90 },
  { key: "range_30d", days: 30 },
  { key: "range_week", days: 7 },
];

const granularityFor = (days: number | null): "day" | "week" =>
  days != null && days <= 30 ? "day" : "week";

export default function Dashboard({
  data,
  onLogout,
  onReload,
  readOnly = false,
  demo = false,
  viewerRole = "self",
  onBack,
  defaultSection = "fitness",
}: {
  data: DashboardData;
  onLogout?: () => void;
  onReload?: () => void;
  readOnly?: boolean;
  demo?: boolean;
  viewerRole?: "self" | "coach";
  onBack?: () => void;
  defaultSection?: "fitness" | "salud" | "coach";
}) {
  const { t } = useUI();
  const [unreadCoach, setUnreadCoach] = useState(0);
  const [planBadge, setPlanBadge] = useState(0);
  const [coachMsgBadge, setCoachMsgBadge] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<"rate_limited" | "timeout" | null>(null);
  const [coachAthlete, setCoachAthlete] = useState<{ id: string; name: string } | null>(null);
  const [coachPopup, setCoachPopup] = useState<{ kind: "msg" | "plan"; id: string; name: string } | null>(null);

  // Nº de actividades por disciplina
  const discCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of disciplinesByActivityCount(data.activities)) m.set(c, 0);
    for (const a of data.activities)
      if (m.has(a.categoria)) m.set(a.categoria, (m.get(a.categoria) ?? 0) + 1);
    return m;
  }, [data.activities]);
  const presentByCount = useMemo(() => disciplinesByActivityCount(data.activities), [data.activities]);
  const mainDisc = useMemo(() => presentByCount.slice(0, 2), [presentByCount]);
  const minorityDisc = useMemo(() => presentByCount.slice(2), [presentByCount]);
  const [added, setAdded] = useState<string[]>(() => data.tab_prefs ?? []);
  const toggleDisc = (c: string) => {
    setAdded((prev) => {
      const next = prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c];
      void api.saveTabPrefs(next).catch(() => {});
      return next;
    });
  };
  const shownDisc = useMemo(() => {
    const set = new Set<string>([...mainDisc, ...added.filter((c) => discCounts.has(c))]);
    return presentByCount.filter((c) => set.has(c));
  }, [mainDisc, added, discCounts, presentByCount]);

  const tabs = useMemo(
    () => [
      { key: "resumen", label: t("tab_resumen"), cat: null },
      ...shownDisc.map((c) => ({ key: c, label: t("cat_" + c), cat: c })),
      { key: "carga", label: t("tab_carga"), cat: null },
      { key: "material", label: t("tab_material"), cat: null },
    ],
    [shownDisc, t]
  );

  const gearMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of data.gear) {
      const label = g.model_name || g.brand || g.id;
      if (label) m[normGearId(g.id)] = label;
    }
    return m;
  }, [data.gear]);

  // Sync
  const onSync = async () => {
    if (syncing || readOnly || demo) return;
    setSyncNotice(null);
    setSyncing(true);
    let before: string | null = null;
    try {
      before = (await api.me()).lastSyncAt;
      await api.sync();
    } catch {
      setSyncing(false);
      return;
    }
    const startedAt = Date.now();
    const drainHistory = async () => {
      for (let step = 0; step < 8 && Date.now() - startedAt < 240000; step++) {
        const result = await api.backfillHistory(1);
        if (result.done || result.pagesProcessed === 0) break;
      }
    };
    const drainDetails = async () => {
      let pending = Number.POSITIVE_INFINITY;
      for (let step = 0; step < 30 && pending > 0 && Date.now() - startedAt < 240000; step++) {
        const order = step % 2 === 0 ? "recent" : "oldest";
        const result = await api.enrichDetails(order, 15);
        pending = result.pending;
        if (result.processed === 0) break;
      }
    };
    const poll = async () => {
      if (Date.now() - startedAt > 180000) {
        setSyncNotice("timeout");
        onReload?.();
        setSyncing(false);
        return;
      }
      await new Promise((r) => setTimeout(r, 3000));
      let now = before;
      try {
        now = (await api.me()).lastSyncAt;
      } catch {}
      if (now && now !== before) {
        try {
          await drainHistory();
          await drainDetails();
        } catch (err) {
          if (err instanceof ApiError && err.status === 429) setSyncNotice("rate_limited");
        }
        onReload?.();
        setSyncing(false);
      } else {
        void poll();
      }
    };
    void poll();
  };

  useEffect(() => {
    if (readOnly) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") !== "1") return;
    params.delete("connected");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    void onSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling no leídos (atleta: mensajes del coach; coach: mensajes de atletas)
  useEffect(() => {
    if (readOnly) return;
    const isCoach = data.account_type === "coach";
    const fetch = () => {
      if (isCoach) {
        coachApi.conversations()
          .then((r) => setCoachMsgBadge(r.conversations.reduce((s, c) => s + c.unread, 0)))
          .catch(() => {});
      } else {
        api.coachMessagesUnread().then((r) => setUnreadCoach(r.unread)).catch(() => {});
      }
    };
    void fetch();
    const id = setInterval(() => { if (!document.hidden) void fetch(); }, 30_000);
    return () => clearInterval(id);
  }, [readOnly, data.account_type]);

  // ──────────────────────────────────────────────
  // Render con AppShell (no readOnly)
  // ──────────────────────────────────────────────
  if (!readOnly) {
    const persona = data.account_type === "coach" ? "coach" : "athlete";
    const isCoach = persona === "coach";
    const navData = { disciplines: shownDisc, unreadCount: isCoach ? coachMsgBadge : unreadCoach + planBadge };
    const athleteName = [data.athlete.first_name, data.athlete.last_name].filter(Boolean).join(" ") || "Westive";
    const roleLabel = t(data.account_type === "coach" ? "profile_role_coach" : "profile_role_athlete");

    const defaultShellSection: SidebarSection =
      defaultSection === "salud" ? "salud" : defaultSection === "coach" ? "coach" : "entrenamiento";
    const defaultShellSub = defaultSection === "salud" ? "s_resumen" : "resumen";

    return (
      <AppShell
        persona={persona}
        navData={navData}
        name={athleteName}
        role={roleLabel}
        onLogout={onLogout ?? (() => {})}
        onSync={onSync}
        syncing={syncing}
        syncNotice={syncNotice}
        lastSyncedAt={demo ? undefined : (data.strava_synced_at ?? undefined)}
        isAdmin={!!data.is_admin}
        devImpersonating={!!data.dev_impersonating}
        devDemoAthletes={data.dev_demo_athletes ?? []}
        onReload={onReload ?? (() => {})}
        readOnly={false}
        defaultSection={defaultShellSection}
        defaultSub={defaultShellSub}
        minorityDisc={minorityDisc}
        discCounts={discCounts}
        addedDisc={added}
        onToggleDisc={toggleDisc}
      >
        {({ section, sub, rangeDays, healthTab, fitnessTab, onSubChange, onSectionChange, openSettings }) => {
          const cutoff = rangeDays != null ? (() => { const d = new Date(); d.setDate(d.getDate() - rangeDays); return d; })() : null;
          const acts = cutoff ? data.activities.filter((a) => new Date(a.start_local) >= cutoff) : data.activities;
          const weekly = cutoff ? data.weekly.filter((w) => new Date(w.week) >= cutoff) : data.weekly;
          const load = cutoff ? data.load.filter((l) => new Date(l.date) >= cutoff) : data.load;
          const health = cutoff ? (data.health ?? []).filter((h) => new Date(h.date) >= cutoff) : (data.health ?? []);
          const granularity = granularityFor(rangeDays);
          const activeTab = tabs.find((x) => x.key === fitnessTab) ?? tabs[0]!;

          // Calendario (top-level)
          if (section === "calendario") {
            // Click en una actividad → su disciplina (o Resumen si no es una tab visible).
            const openActivity = (cat: string) =>
              onSectionChange("entrenamiento", tabs.some((x) => x.cat === cat) ? cat : "resumen");
            return <><Calendario acts={data.activities} onOpenActivity={openActivity} /><FooterModule /></>;
          }

          // Coach: Red → Atletas / Códigos
          if (section === "red" && persona === "coach") {
            if (sub === "codigos") return <><CoachLinkCodes /><FooterModule /></>;
            if (sub === "mensajes") return <><CoachInbox /><FooterModule /></>;
            // sub === "atletas" — con vista contextual de atleta abierto
            if (coachAthlete) {
              return (
                <CoachAthleteView
                  athleteId={coachAthlete.id}
                  initialSection="fitness"
                  onBack={() => setCoachAthlete(null)}
                />
              );
            }
            return (
              <>
                <CoachRoster
                  onSelect={(id, name) => setCoachAthlete({ id, name })}
                  onMessage={(id, name) => setCoachPopup({ kind: "msg", id, name })}
                  onCalendar={(id, name) => setCoachPopup({ kind: "plan", id, name })}
                />
                <FooterModule />
                {coachPopup && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCoachPopup(null)} />
                    <div className="relative z-10 flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-line bg-panel shadow-2xl">
                      <div className="flex items-center justify-between border-b border-line px-4 py-3">
                        <span className="font-semibold text-fg">{coachPopup.name}</span>
                        <button onClick={() => setCoachPopup(null)} className="rounded-lg border border-line px-2 py-1 text-xs text-muted transition hover:text-fg">✕</button>
                      </div>
                      <div className="overflow-y-auto p-4">
                        {coachPopup.kind === "msg" && (
                          <DirectChat
                            viewerRole="coach"
                            fetchMessages={() => coachApi.messages(coachPopup.id)}
                            sendMessage={(b) => coachApi.sendMessage(coachPopup.id, b)}
                          />
                        )}
                        {coachPopup.kind === "plan" && <CoachCalendar athleteId={coachPopup.id} />}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          }

          // Atleta: Red → Mi entrenador
          if (section === "red") {
            return (
              <MyCoach
                onUnseenCount={(n) => setPlanBadge(n)}
                onNavigateCalendar={() => onSectionChange("calendario")}
                demo={demo}
                coachData={demo ? DEMO_COACH : undefined}
              />
            );
          }

          const noActivities = acts.length === 0;
          const noHealth = health.length === 0;
          const hasConn = data.has_connections !== false;

          return (
            <>
              {/* Contenido */}
              {section === "entrenamiento" && activeTab.key === "resumen" && (
                noActivities
                  ? <EmptyState title="empty_no_activities" desc="empty_no_activities_desc" hasConnections={hasConn} onConnect={hasConn ? undefined : openSettings} />
                  : <Resumen acts={acts} weekly={weekly} gearMap={gearMap} granularity={granularity} />
              )}
              {section === "entrenamiento" && activeTab.cat === "Musculacion" && (
                <BodyComposition />
              )}
              {section === "entrenamiento" && activeTab.cat && activeTab.cat !== "Musculacion" && (
                noActivities
                  ? <EmptyState title="empty_no_activities" desc="empty_no_activities_desc" hasConnections={hasConn} onConnect={hasConn ? undefined : openSettings} />
                  : <Discipline cat={activeTab.cat} acts={acts} zones={data.zones} gearMap={gearMap} granularity={granularity} />
              )}
              {section === "entrenamiento" && activeTab.key === "material" && (
                data.gear.length === 0
                  ? <EmptyState title="empty_no_gear" desc="empty_no_gear_desc" hasConnections={hasConn} onConnect={hasConn ? undefined : openSettings} />
                  : <Material data={data} />
              )}
              {section === "entrenamiento" && activeTab.key === "carga" && (
                <Carga load={load} acts={acts} gearMap={gearMap} />
              )}
              {section === "salud" && (
                noHealth
                  ? <EmptyState title="empty_no_health" desc="empty_no_health_desc" hasConnections={hasConn} onConnect={hasConn ? undefined : openSettings} />
                  : <Salud
                      view={healthTab}
                      health={health}
                      load={load}
                      hrIntraday={data.hr_intraday ?? []}
                      athlete={data.athlete}
                    />
              )}
              {section === "coach" && (
                demo
                  ? <DemoLockedSection t={t} />
                  : <Coach initialPrompt={null} />
              )}
              <FooterModule />
            </>
          );
        }}
      </AppShell>
    );
  }

  // ──────────────────────────────────────────────
  // Render simplificado (readOnly — usado por CoachAthleteView)
  // ──────────────────────────────────────────────
  const [section] = useState<"fitness" | "salud" | "coach">(defaultSection);
  const [tab, setTab] = useState<string>("resumen");
  const [healthTab, setHealthTab] = useState<HealthView>("s_resumen");
  const [rangeDays, setRangeDays] = useState<number | null>(null);

  const activeTab = tabs.find((x) => x.key === tab) ?? tabs[0]!;
  const cutoff = useMemo(() => {
    if (rangeDays === null) return null;
    const d = new Date();
    d.setDate(d.getDate() - rangeDays);
    return d;
  }, [rangeDays]);
  const acts = useMemo(() => cutoff ? data.activities.filter((a) => new Date(a.start_local) >= cutoff) : data.activities, [data.activities, cutoff]);
  const weekly = useMemo(() => cutoff ? data.weekly.filter((w) => new Date(w.week) >= cutoff) : data.weekly, [data.weekly, cutoff]);
  const load = useMemo(() => cutoff ? data.load.filter((l) => new Date(l.date) >= cutoff) : data.load, [data.load, cutoff]);
  const health = useMemo(() => { const all = data.health ?? []; return cutoff ? all.filter((h) => new Date(h.date) >= cutoff) : all; }, [data.health, cutoff]);
  const granularity = granularityFor(rangeDays);

  const readOnlyAccent = section === "salud"
    ? ({ s_resumen: "20 184 166", s_cuerpo: "16 185 129", s_sueno: "99 102 241", s_cruce: "139 92 246" }[healthTab] ?? "20 184 166")
    : (activeTab.cat ? catAccentRGB(activeTab.cat) : { resumen: "252 76 2", carga: "239 68 68", material: "34 197 94", calendario: "234 179 8" }[activeTab.key] ?? "252 76 2");

  return (
    <div
      className="px-4 py-4"
      style={{ ["--accent" as string]: readOnlyAccent } as React.CSSProperties}
    >
      {section !== "coach" && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-line">
          <nav className="flex flex-wrap items-center gap-1">
            {section === "fitness" &&
              tabs.map((x) => (
                <React.Fragment key={x.key}>
                  {x.key === "carga" && minorityDisc.length > 0 && (
                    <AddDisciplineMenu minority={minorityDisc} counts={discCounts} added={added} onToggle={toggleDisc} />
                  )}
                  <button
                    onClick={() => setTab(x.key)}
                    className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition ${
                      activeTab.key === x.key ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"
                    }`}
                  >
                    {x.label}
                  </button>
                </React.Fragment>
              ))}
            {section === "salud" &&
              HEALTH_TABS.map((x) => (
                <button
                  key={x}
                  onClick={() => setHealthTab(x)}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition ${
                    healthTab === x ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"
                  }`}
                >
                  {t("tab_" + x)}
                </button>
              ))}
          </nav>
          {!(section === "fitness" && (activeTab.key === "material" || activeTab.key === "calendario")) && (
            <Segmented
              ariaLabel="range"
              variant="plain"
              value={String(rangeDays)}
              onChange={(v) => setRangeDays(v === "null" ? null : Number(v))}
              options={RANGES.map((r) => ({ value: String(r.days), label: t(r.key) }))}
            />
          )}
        </div>
      )}

      {section === "fitness" && activeTab.key === "resumen" && (
        <Resumen acts={acts} weekly={weekly} gearMap={gearMap} granularity={granularity} />
      )}
      {section === "fitness" && activeTab.cat === "Musculacion" && <BodyComposition />}
      {section === "fitness" && activeTab.cat && activeTab.cat !== "Musculacion" && (
        <Discipline cat={activeTab.cat} acts={acts} zones={data.zones} gearMap={gearMap} granularity={granularity} />
      )}
      {section === "fitness" && activeTab.key === "material" && <Material data={data} />}
      {section === "fitness" && activeTab.key === "carga" && <Carga load={load} acts={acts} gearMap={gearMap} />}
      {section === "salud" && (
        <Salud view={healthTab} health={health} load={load} hrIntraday={data.hr_intraday ?? []} athlete={data.athlete} />
      )}
    </div>
  );
}

function DemoLockedSection({ t }: { t: (k: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <span className="text-3xl">🔒</span>
      <p className="text-base font-semibold text-fg">{t("demo_locked")}</p>
    </div>
  );
}

